let libpartyPromise = null;

export const ANALYSIS_TARGET_SAMPLE_RATE = 16_000;

async function loadLibparty() {
    if (libpartyPromise) {
        return libpartyPromise;
    }

    libpartyPromise = import(/* webpackIgnore: true */ '/libparty/libparty.js')
        .then(async (module) => {
            await module.default();
            return module;
        })
        .catch((error) => {
            libpartyPromise = null;
            throw error;
        });

    return libpartyPromise;
}

export async function analyzeAudio(preparedAudio) {
    const results = {
        bpm: null,
        scale: {
            key: null,
            mode: null
        },
        lufs: null
    };

    try {
        const { analyze_stereo, analyze_mono } = await loadLibparty();
        const analysis = preparedAudio.right
            ? analyze_stereo(preparedAudio.left, preparedAudio.right, preparedAudio.sampleRate)
            : analyze_mono(preparedAudio.left, preparedAudio.sampleRate);

        results.bpm = analysis.bpm ?? null;
        results.lufs = Number.isFinite(analysis.integrated_lufs)
            ? analysis.integrated_lufs
            : null;
        results.scale.key = analysis.key ?? null;
        results.scale.mode = analysis.mode ?? null;
    } catch (error) {
        console.warn('libparty analysis failed:', error);
    }

    return results;
}

export function prepareAudioForAnalysis(audioBuffer, targetSampleRate = ANALYSIS_TARGET_SAMPLE_RATE) {
    const sampleRateIn = audioBuffer.sampleRate;
    const needsResample = sampleRateIn !== targetSampleRate;
    const leftSource = audioBuffer.getChannelData(0);
    const rightSource = audioBuffer.numberOfChannels > 1
        ? audioBuffer.getChannelData(1)
        : null;

    if (!needsResample) {
        return {
            left: leftSource,
            right: rightSource,
            sampleRate: sampleRateIn
        };
    }

    return {
        left: resampleLinear(leftSource, sampleRateIn, targetSampleRate),
        right: rightSource ? resampleLinear(rightSource, sampleRateIn, targetSampleRate) : null,
        sampleRate: targetSampleRate
    };
}

export function generateWaveformDataUrlFromChannels(left, right, options = {}) {
    const width = options.width || 500;
    const height = options.height || 80;
    const color = options.color || '#000000';
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (!ctx || !(left instanceof Float32Array) || left.length === 0) {
        return null;
    }

    const rightChannel = right instanceof Float32Array ? right : left;
    const segmentLen = Math.max(1, Math.floor(left.length / width));
    const halfHeight = height / 2;
    const bars = new Array(width);
    let maxAmp = 0;

    for (let i = 0; i < width; i += 1) {
        const start = i * segmentLen;
        const len = Math.min(segmentLen, left.length - start);
        if (len <= 0) {
            bars[i] = [0, 0];
            continue;
        }
        const pos = bufferMeasure(left, start, len);
        const neg = bufferMeasure(rightChannel, start, len);
        bars[i] = [pos, neg];
        maxAmp = Math.max(maxAmp, pos, neg);
    }

    ctx.fillStyle = color;
    ctx.fillRect(0, Math.floor(halfHeight - 1), width, 1);

    if (maxAmp <= 0) {
        return canvas.toDataURL('image/png');
    }

    const scale = halfHeight / maxAmp;
    for (let i = 0; i < width; i += 1) {
        const [pos, neg] = bars[i];
        const posPx = Math.max(1, Math.ceil(pos * scale));
        const negPx = Math.max(1, Math.ceil(neg * scale));
        ctx.fillRect(i, Math.floor(halfHeight - posPx), 1, posPx);
        ctx.fillRect(i, Math.floor(halfHeight), 1, negPx);
    }

    return canvas.toDataURL('image/png');
}

export function dataUrlToBlob(dataUrl) {
    const [header, base64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)[1];
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
}

function resampleLinear(input, sampleRateIn, sampleRateOut) {
    if (!(input instanceof Float32Array) || input.length === 0) {
        return new Float32Array(0);
    }
    if (sampleRateIn === sampleRateOut) {
        return input;
    }

    const outputLength = Math.max(1, Math.round((input.length * sampleRateOut) / sampleRateIn));
    if (outputLength === 1) {
        return new Float32Array([input[0]]);
    }

    const output = new Float32Array(outputLength);
    const factor = (input.length - 1) / (outputLength - 1);
    for (let i = 0; i < outputLength; i += 1) {
        const position = i * factor;
        const index = Math.floor(position);
        const fraction = position - index;
        const next = Math.min(index + 1, input.length - 1);
        output[i] = input[index] * (1 - fraction) + input[next] * fraction;
    }
    return output;
}

function bufferMeasure(data, start, len) {
    let sum = 0;
    const end = start + len;
    for (let i = start; i < end; i += 1) {
        sum += data[i] * data[i];
    }
    return Math.sqrt(sum / len);
}
